import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
} from "@angular/core";
import {
  FormBuilder,
  FormGroup,
  Validators,
  FormsModule,
  ReactiveFormsModule,
} from "@angular/forms";
import {WorksService} from "../../../shared/services/works.service";
import {WorksModel} from "src/app/models/WorksModel";
import {AuthService} from "src/app/modules/auth/services/auth.service";
import {User} from "src/app/modules/auth/models/User";
import {ModalService} from "../../services/modal.service";
import {Subscription, finalize} from "rxjs";
import {DateAdapter} from "@angular/material/core";
import {MyDateAdapter} from "src/app/helpers/MyDateAdapter";
import {ToastService} from "../../services/toast.service";
import {TranslateService, TranslateModule} from "@ngx-translate/core";
import {WorksTypes} from "src/app/enums/Works.enum";
import {MatFormField, MatHint, MatSuffix} from "@angular/material/form-field";
import {MatInput} from "@angular/material/input";
import {
  MatDatepickerInput,
  MatDatepickerToggle,
  MatDatepicker,
} from "@angular/material/datepicker";
import {MatCheckbox} from "@angular/material/checkbox";
import {MatSlideToggle} from "@angular/material/slide-toggle";
import {
  clampDateToToday,
  getDateSafely,
  getWorkReleaseDateSafely,
} from "src/app/helpers/date";

@Component({
  selector: "app-edit-work-date",
  templateUrl: "./edit-work-date.component.html",
  styleUrls: ["./edit-work-date.component.css"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [{provide: DateAdapter, useClass: MyDateAdapter}],
  imports: [
    FormsModule,
    ReactiveFormsModule,
    MatFormField,
    MatInput,
    MatDatepickerInput,
    MatHint,
    MatDatepickerToggle,
    MatSuffix,
    MatDatepicker,
    MatCheckbox,
    MatSlideToggle,
    TranslateModule,
  ],
})
export class EditWorkDateComponent implements OnInit, OnDestroy, OnChanges {
  @Input() item!: WorksModel;
  dateForm: FormGroup;
  user!: User;
  public worksTypes = WorksTypes;
  public submitText?: string;
  public readonly today = new Date();
  public discoveredOnRelease = false;
  public saving = false;
  private readonly subscriptions: Subscription[] = [];
  private manualDiscoveryDate = new Date();

  constructor(
    private readonly translate: TranslateService,
    private readonly fb: FormBuilder,
    private readonly worksService: WorksService,
    private readonly AuthService: AuthService,
    private readonly modalService: ModalService,
    private readonly toastService: ToastService,
    private readonly cdr: ChangeDetectorRef
  ) {
    this.dateForm = this.fb.group({
      selectedDate: ["", Validators.required],
      markCompleted: [false, Validators.required],
    });

    const userSubscription = this.AuthService.user$.subscribe({
      next: (user: User | null) => {
        if (user) {
          this.user = user;
          this.cdr.markForCheck();
        }
      },
    });
    this.subscriptions.push(userSubscription);
  }

  ngOnInit() {
    this.setDiscoveryDate(this.item);
    this.setSubmitText("SAVE");

    const translateSubscription = this.translate.onLangChange.subscribe(() => {
      this.setSubmitText("SAVE");
      this.cdr.markForCheck();
    });
    this.subscriptions.push(translateSubscription);
  }

  ngOnChanges() {
    if (this.item && !this.dateForm.dirty) {
      this.setDiscoveryDate(this.item);
    }
  }

  private setSubmitText(key: string): void {
    const translateSubscription = this.translate
      .get(key)
      .subscribe((translatedText) => {
        this.submitText = translatedText;
        this.cdr.markForCheck();
      });
    this.subscriptions.push(translateSubscription);
  }

  setDiscoveryDate(item: WorksModel) {
    if (!this.dateForm.dirty) {
      const discoveryDate = getDateSafely(item?.discoveryDate) ?? new Date();
      this.discoveredOnRelease = false;
      this.manualDiscoveryDate = discoveryDate;
      this.dateForm.get("selectedDate")?.enable({emitEvent: false});
      this.dateForm.patchValue({selectedDate: discoveryDate});
    }
  }

  public get workReleaseDate(): Date | null {
    if (!this.item) {
      return null;
    }

    const releaseDate = this.worksService.getReleaseDateBasedOnCountry(
      this.item,
      this.user?.country ?? "US"
    );
    return getWorkReleaseDateSafely(releaseDate);
  }

  public onDiscoveredOnReleaseChange(checked: boolean): void {
    const selectedDateControl = this.dateForm.get("selectedDate");
    const releaseDate = this.workReleaseDate;
    if (checked && !releaseDate) {
      return;
    }

    this.discoveredOnRelease = checked;
    if (checked && releaseDate) {
      const selectedDate = getDateSafely(selectedDateControl?.value);
      if (selectedDate) {
        this.manualDiscoveryDate = selectedDate;
      }
      selectedDateControl?.setValue(releaseDate);
      selectedDateControl?.disable();
    } else {
      selectedDateControl?.enable();
      selectedDateControl?.setValue(this.manualDiscoveryDate);
    }

    this.dateForm.markAsDirty();
    this.cdr.markForCheck();
  }

  onSubmit() {
    if (this.saving || this.dateForm.invalid) return;

    const selectedDate = this.dateForm.get("selectedDate")?.value;
    const completedOnDiscoveryDate = this.dateForm.get("markCompleted")?.value;

    const releaseDate = this.workReleaseDate;
    if (!selectedDate) return;

    const clampedSelectedDate = clampDateToToday(selectedDate);
    const adjustedDate = new Date(
      Math.max(clampedSelectedDate.getTime(), releaseDate?.getTime() ?? 0)
    );
    const finalDate = clampDateToToday(adjustedDate);
    const adjustedDateStr: string = finalDate.toISOString();
    this.dateForm.setValue({
      selectedDate: finalDate,
      markCompleted: completedOnDiscoveryDate,
    });
    this.saving = true;
    this.setSubmitText("SAVING");

    const updateWorkSubscription = this.worksService
      .updateWorkDateByType(
        this.user._id,
        this.item._id,
        this.item.type,
        adjustedDateStr,
        releaseDate ?? undefined,
        completedOnDiscoveryDate,
        this.discoveredOnRelease
      )
      .pipe(
        finalize(() => {
          this.saving = false;
          this.submitText = this.translate.instant("SAVE");
          this.cdr.markForCheck();
        })
      )
      .subscribe({
        next: (user: boolean | null) => {
          if (user) {
            this.setSubmitText("SAVE");
            this.item = {...this.item, discoveryDate: finalDate};

            this.modalService.updateItem(this.item);
            this.worksService.setEditedItem(this.item);

            this.toastService.sendToast({
              text: "WORK_UPDATED",
              classes: "bg-form-success",
            });
            this.cdr.markForCheck();

            this.modalService.closeModal("ewd_" + this.item._id);
          }
        },
        error: () => {
          this.setSubmitText("SAVE");
          this.toastService.sendToast({
            text: "ERROR_UPDATE",
            classes: "bg-form-error",
          });
        },
      });
    this.subscriptions.push(updateWorkSubscription);
  }

  ngOnDestroy() {
    for (const sub of this.subscriptions) {
      sub.unsubscribe();
    }
  }
}
